<script lang="ts" setup>
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import LogoSmall from '@exchange/assets/one-trading-white-icon.svg';
import Logo from '@exchange/assets/one-trading-white-logo.svg';
import { authService } from '@exchange/libs/auth/service/src';
import { LoginButtons } from '@exchange/libs/auth/ui/src';
import PrivacyButton from '@exchange/libs/balances/ui/src/lib/PrivacyButton.vue';
import { FundingActionsTriggers } from '@exchange/libs/components/shared/src';
import useMQBreakpoints from '@exchange/libs/composables/shared/src/lib/useMQBreakpoints';
import LanguageSetting from '@exchange/libs/settings/ui/src/lib/account/LanguageSetting.vue';

import AppHeaderNavLinks, { TradeNavLink } from './AppHeaderNavLinks.vue';
import PandaMenu from './PandaMenu.vue';

const route = useRoute();
const router = useRouter();
const { isSMAndDown } = useMQBreakpoints();

const hasBeenAuthenticated = computed(() => authService.hasBeenAuthenticated);
const logo = computed(() => (isSMAndDown.value ? { component: LogoSmall, postfix: 'small' } : { component: Logo, postfix: 'big' }));

const navigateToExchange = () => {
  if (route.name === 'exchange') {
    return;
  }

  router.push({
    name: 'exchange',
  });
};

const onLinkClick = (v: TradeNavLink) => {
  let routeName: string;

  switch (v) {
    case TradeNavLink.futures:
      routeName = 'futures';
      break;
    case TradeNavLink.instantTrade:
      routeName = 'portfolio';
      break;
    case TradeNavLink.spot:
    default:
      routeName = 'exchange';
      break;
  }

  if (!routeName) {
    return;
  }

  router.push({
    name: routeName,
  });
};
</script>

<template>
  <div
    id="application-nav-bar"
    class="app-header"
    testid="app-nav-bar"
  >
    <component
      :is="logo.component"
      class="app-header__logo"
      :class="`app-header__logo--${logo.postfix}`"
      @click="navigateToExchange"
    />
    <div class="app-header__links">
      <app-header-nav-links @link-clicked="onLinkClick" />
    </div>
    <template v-if="hasBeenAuthenticated">
      <funding-actions-triggers
        class="app-header__funding-actions"
        :short="true"
      />
      <div class="app-header__separator" />
    </template>

    <language-setting
      class="app-header__language-select"
      :toggle="true"
    />
    <privacy-button
      v-if="hasBeenAuthenticated"
      button-variant="tertiary"
      class="app-header__privacy-button"
    />
    <panda-menu v-if="hasBeenAuthenticated" />
    <login-buttons
      v-else
      class="app-header__login"
    />
  </div>
</template>

<style lang="scss" scoped>
.app-header {
  display: flex;
  width: auto;
  height: var(--app-header-height);
  min-height: var(--app-header-height);
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  padding: 8px;
  border-bottom: 3px solid rgb(var(--v-theme-elevation-0));
  background-color: rgb(var(--v-theme-elevation-0));
  color: rgb(var(--v-theme-text-primary));

  &__logo {
    display: flex;
    align-items: center;
    margin-left: 8px;
    cursor: pointer;

    &--big {
      width: 170px;
      height: 22px;
    }

    &--small {
      width: 28px;
    }
  }

  &__links {
    display: flex;
    margin: 0 auto 0 28px;
  }

  &__market-search {
    flex-shrink: 0;
  }

  &__separator {
    width: 2px;
    height: 100%;
    margin: 0 8px;
    background-color: rgb(var(--v-theme-elevation-2));
  }

  &__language-select {
    --x-select-z-index-custom: var(--app-header-select-z-index);

    flex-shrink: 0;
  }

  &__privacy-button {
    margin: 0 8px;
  }

  &__login {
    flex: 0 1 290px;
    margin-left: 16px;
  }
}
</style>
